import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import WarningIcon from '@mui/icons-material/Warning';
import { useAuth } from '../../context/AuthContext';
import config from '../../config/config';

const ReparentDialog = ({ open, onClose, onConfirm, childUser, oldParent, newParent, loading }) => {
  const { getAccessToken } = useAuth();
  const [updateTeam, setUpdateTeam] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);

  useEffect(() => {
    if (open && newParent) {
      setTeamName(newParent.teamName || '');
      setUpdateTeam(false);
    }
  }, [open, newParent]);

  useEffect(() => {
    if (open && updateTeam && teams.length === 0) {
      fetchTeams();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, updateTeam]);

  const fetchTeams = async () => {
    setTeamsLoading(true);
    try {
      const token = await getAccessToken();
      if (!token) return;

      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/teams`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        const data = result.data || result;
        setTeams(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      // silently fail — dropdown will just be empty
    } finally {
      setTeamsLoading(false);
    }
  };

  const handleConfirm = () => {
    onConfirm({
      userId: childUser.id,
      newUplinePlatinumIbo: newParent.iboNumber,
      teamName: updateTeam ? teamName : null,
    });
  };

  if (!childUser || !newParent) return null;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SwapHorizIcon color="primary" />
        Reparent User
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          This will mark the user as <strong>override</strong> — Amway KPI will
          not change this user's upline automatically until the override is
          cleared.
        </Alert>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            my: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{
              textAlign: 'center',
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              minWidth: 140,
            }}
          >
            <Typography variant="subtitle2" fontWeight={600}>
              {childUser.iboName || 'Unknown'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              IBO: {childUser.iboNumber}
            </Typography>
            {childUser.teamName && (
              <Box sx={{ mt: 0.5 }}>
                <Chip label={childUser.teamName} size="small" />
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Current Upline
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {oldParent
                ? `${oldParent.iboName || 'Unknown'} (${oldParent.iboNumber})`
                : 'None (root)'}
            </Typography>
          </Box>
          <SwapHorizIcon color="action" />
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">
              New Upline
            </Typography>
            <Typography variant="body2" fontWeight={500} color="primary">
              {newParent.iboName || 'Unknown'} ({newParent.iboNumber})
            </Typography>
          </Box>
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={updateTeam}
              onChange={(e) => setUpdateTeam(e.target.checked)}
            />
          }
          label="Also update team name"
        />

        {updateTeam && (
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>Team Name</InputLabel>
            <Select
              value={teamName}
              label="Team Name"
              onChange={(e) => setTeamName(e.target.value)}
              disabled={teamsLoading}
              startAdornment={teamsLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
            >
              {teams.map((t) => (
                <MenuItem key={t.id} value={t.name}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {t.name}
                    {t.name === newParent.teamName && (
                      <Chip label="upline's team" size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
                    )}
                    {t.name === childUser.teamName && (
                      <Chip label="current" size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
            {newParent.teamName && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 0.5 }}>
                New parent's team: {newParent.teamName}
              </Typography>
            )}
          </FormControl>
        )}

        {childUser.uplineOverride && (
          <Alert severity="warning" icon={<WarningIcon />} sx={{ mt: 2 }}>
            This user already has an active override. Reparenting will update the
            override with the new upline.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Updating...' : 'Confirm Reparent'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReparentDialog;
